/**
 * Admin API client.
 *
 * The panel is a separate origin from the API, so every request sends the
 * session cookie with `credentials: 'include'`. Admin access also requires the
 * session role to be `admin` (configured in `catalog_meta.admin_ids`).
 */

export const API_BASE: string =
  import.meta.env.VITE_API_BASE_URL ?? 'https://appwin-api.korova313131.workers.dev';

export interface Session {
  developerId: string;
  email: string;
  displayName: string;
  role: 'dev' | 'admin';
}

export interface AdminApp {
  id: string;
  name: string;
  category: string;
  origin: 'native' | 'winget';
  status: string;
  unverified: boolean;
  latestVersion: string | null;
  releaseCount: number;
  updatedAt: number;
}

export interface AdminRelease {
  id: string;
  version: string;
  kind: string;
  scope: string;
  arch: string[];
  sizeBytes: number;
  sha256: string;
  downloadUrl: string | null;
  silentArgs: string[] | null;
  status: string;
  publishedAt: number | null;
  rejectReason: string | null;
  yankReason: string | null;
}

export interface AdminAppDetail {
  app: {
    id: string;
    name: string;
    category: string;
    summary: Record<string, string>;
    description: Record<string, string>;
    homepageUrl: string | null;
    license: string | null;
    origin: 'native' | 'winget';
    status: string;
    unverified: boolean;
    updatedAt: number;
  };
  releases: AdminRelease[];
}

export interface ModerationItem {
  release: {
    id: string;
    version: string;
    kind: string;
    scope: string;
    sizeBytes: number;
    downloadUrl: string | null;
    avStatus: string;
  };
  appId: string;
  appName: string;
  submittedAt: number;
  checks: { sha256: string; av: string; authenticode: string; peMagic: string };
}

export interface Stats {
  windowDays: number;
  installs: { total: number; success: number; failure: number };
  updates: { total: number; success: number; failure: number };
  successRate: number | null;
  topApps: Array<{ appId: string; name: string; installs: number; failures: number }>;
  topErrors: Array<{ errorCode: string; count: number }>;
  pendingModeration: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}/v1${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });
  if (response.status === 204) return undefined as T;
  if (!response.ok) {
    let code = 'internal';
    let message = `Ошибка (${response.status})`;
    try {
      const body = (await response.json()) as { error?: { code?: string; message?: string } };
      code = body.error?.code ?? code;
      message = body.error?.message ?? message;
    } catch {
      // keep the status-based message
    }
    throw new ApiError(response.status, code, message);
  }
  return (await response.json()) as T;
}

export interface CreateAppInput {
  id: string;
  name: string;
  category: string;
  summary: Record<string, string>;
  description: Record<string, string>;
  homepageUrl?: string;
  license?: string;
}

export interface AddVersionInput {
  version: string;
  platform: 'windows';
  arch: Array<'x86' | 'x64' | 'arm64' | 'universal'>;
  kind: 'msix' | 'appx' | 'msi' | 'exe' | 'zip';
  scope: 'per_user' | 'machine' | 'portable';
  downloadUrl: string;
  sha256: string;
  sizeBytes?: number;
  silentArgs?: string[];
}

export const api = {
  session: () => request<Session>('/auth/session'),
  signInWithGithub(): void {
    window.location.href = `${API_BASE}/v1/auth/github/start`;
  },
  logout: () => request<{ signedOut: boolean }>('/auth/logout', { method: 'POST' }),

  apps: (query?: string, category?: string) => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (category) params.set('category', category);
    const suffix = params.toString() ? `?${params.toString()}` : '';
    return request<{ items: AdminApp[] }>(`/admin/apps${suffix}`);
  },
  createApp: (input: CreateAppInput) =>
    request<{ id: string }>('/admin/apps', { method: 'POST', body: JSON.stringify(input) }),
  updateApp: (
    appId: string,
    patch: {
      name?: string;
      category?: string;
      summary?: Record<string, string>;
      description?: Record<string, string>;
      homepageUrl?: string;
      license?: string;
    },
  ) => {
    const [developer, app] = appId.split('/');
    return request<{ updated: boolean }>(`/admin/apps/${developer}/${app}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  },
  addVersion: (appId: string, input: AddVersionInput) => {
    const [developer, app] = appId.split('/');
    return request<{ created: boolean }>(`/admin/apps/${developer}/${app}/releases`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  app: (appId: string) => {
    const [developer, app] = appId.split('/');
    return request<AdminAppDetail>(`/admin/apps/${developer}/${app}`);
  },
  updateRelease: (
    releaseId: string,
    patch: {
      downloadUrl?: string;
      sha256?: string;
      sizeBytes?: number;
      silentArgs?: string[];
      status?: 'published' | 'yanked';
    },
  ) =>
    request<{ updated: boolean }>(`/admin/releases/${releaseId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  deleteRelease: (releaseId: string) =>
    request<{ deleted: boolean }>(`/admin/releases/${releaseId}`, { method: 'DELETE' }),
  removeApp: (appId: string) => {
    const [developer, app] = appId.split('/');
    return request<{ delisted: boolean }>(`/admin/apps/${developer}/${app}`, { method: 'DELETE' });
  },

  moderation: () => request<{ items: ModerationItem[] }>('/admin/moderation'),
  approve: (releaseId: string) =>
    request<{ published: boolean }>(`/admin/releases/${releaseId}/approve`, { method: 'POST' }),
  reject: (releaseId: string, reason: string) =>
    request<{ rejected: boolean }>(`/admin/releases/${releaseId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  runWingetImport: () =>
    request<{ queued: boolean }>('/admin/imports/winget', {
      method: 'POST',
      body: JSON.stringify({ curatedOnly: true, source: 'winget' }),
    }),

  stats: () => request<Stats>('/admin/stats'),
};
